import { onMount, onCleanup } from 'solid-js';
import { useVim } from './VimProvider';
import { VIM_KEY_MAP } from './types';

/**
 * Vim Keyboard Handler Component
 * Captures and processes vim key bindings globally
 */
export function VimKeyboardHandler() {
  const vim = useVim();

  onMount(() => {
    let keyBuffer = '';
    let bufferTimeout: number | undefined;

    const clearKeyBuffer = () => {
      keyBuffer = '';
    };

    const resetBuffer = () => {
      if (bufferTimeout) {
        window.clearTimeout(bufferTimeout);
      }
      bufferTimeout = window.setTimeout(clearKeyBuffer, 1000);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const state = vim.state();

      // If vim is not enabled, don't handle any keys
      if (!state.enabled) {
        return;
      }

      const target = e.target as HTMLElement;

      // Check if user is typing in an input/textarea
      const isInputActive =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Handle Escape key - close modals first, then go to normal mode
      if (e.key === 'Escape') {
        e.preventDefault();

        // Check if project modal is open and close it
        const projectModal = document.querySelector('.rohith-project-modal-overlay');
        if (projectModal) {
          // Find and click the close button to trigger the close handler
          const closeButton = projectModal.querySelector('.rohith-project-modal-close');
          if (closeButton instanceof HTMLElement) {
            closeButton.click();
            return;
          }
        }

        // Check if vim help panel is open and close it
        if (vim.state().showHelp) {
          vim.toggleHelp();
          return;
        }

        vim.setMode('normal');
        vim.clearBuffer();
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        keyBuffer = '';
        return;
      }

      // In insert mode, only handle Escape
      if (state.mode === 'insert') {
        if (e.key === 'Escape') {
          e.preventDefault();
          vim.setMode('normal');
          vim.clearBuffer();
        }
        return;
      }

      // In command mode, handle command input
      if (state.mode === 'command') {
        if (e.key === 'Enter') {
          e.preventDefault();
          // Check if this is a search command
          if (state.commandBuffer.startsWith('/') || state.commandBuffer.startsWith('?')) {
            vim.performSearch(state.commandBuffer);
          } else {
            processCommand(state.commandBuffer);
          }
          vim.setMode('normal');
          vim.clearBuffer();
          return;
        }

        if (e.key === 'Backspace') {
          e.preventDefault();
          const newBuffer = state.commandBuffer.slice(0, -1);
          vim.clearBuffer();
          // Rebuild buffer
          newBuffer.split('').forEach(char => vim.appendToBuffer(char));
          return;
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          vim.setMode('normal');
          vim.clearBuffer();
          return;
        }

        // Append printable characters
        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          vim.appendToBuffer(e.key);
          return;
        }
        return;
      }

      // In normal/visual mode, handle vim keys
      if (state.mode === 'normal' || state.mode === 'visual') {
        // Ignore if typing in input (unless we just focused it)
        if (isInputActive && !e.ctrlKey && !e.altKey && !e.metaKey) {
          return;
        }

        // Handle ? key first (before VIM_KEY_MAP check) for toggle help
        if (e.key === '?' && keyBuffer === '') {
          e.preventDefault();
          vim.toggleHelp();
          keyBuffer = '';
          return;
        }

        // Handle number keys for repeat counts
        if (/^[0-9]$/.test(e.key) && keyBuffer === '') {
          e.preventDefault();
          keyBuffer += e.key;
          resetBuffer();
          return;
        }

        // Build key combination
        const keyCombo = e.ctrlKey ? `ctrl-${e.key.toLowerCase()}` : keyBuffer + e.key;

        // Check for 'gg' special case
        if (keyBuffer === 'g' && e.key === 'g') {
          e.preventDefault();
          vim.executeCommand('moveDocumentStart', parseInt(keyBuffer) || 1);
          keyBuffer = '';
          return;
        }

        // Check if this is a vim command
        const command = VIM_KEY_MAP[keyCombo];

        if (command) {
          e.preventDefault();
          const count = parseInt(keyBuffer) || 1;
          vim.executeCommand(command, count);
          keyBuffer = '';
          if (bufferTimeout) {
            window.clearTimeout(bufferTimeout);
          }
          return;
        }

        // If 'g' is pressed, buffer it for 'gg'
        if (e.key === 'g' && keyBuffer === '') {
          e.preventDefault();
          keyBuffer = 'g';
          resetBuffer();
          return;
        }

        if (e.key === 'Enter') {
          e.preventDefault();
          vim.executeCommand('click');
          keyBuffer = '';
          return;
        }

        // Buffer the key for potential multi-key sequences
        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
          keyBuffer += e.key;
          resetBuffer();
        }
      }
    };

    // Process command mode input
    const processCommand = (cmd: string) => {
      const command = cmd.toLowerCase().trim();

      // q or quit - close help
      if (command === ':q' || command === ':quit') {
        if (vim.state().showHelp) {
          vim.toggleHelp();
        }
        return;
      }

      // h or help - show help
      if (command === ':h' || command === ':help' || command === '?') {
        if (!vim.state().showHelp) {
          vim.toggleHelp();
        }
        return;
      }

      // w or write - save (placeholder)
      if (command === ':w' || command === ':write') {
        // Could trigger a save action
        return;
      }

      // Theme commands
      if (command.startsWith(':theme')) {
        const theme = command.split(' ')[1];
        if (theme) {
          // Could dispatch theme change
          void theme;
        }
        return;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown, true);

    onCleanup(() => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (bufferTimeout) {
        window.clearTimeout(bufferTimeout);
      }
    });
  });

  return null;
}
